import { useEffect, useRef, useState } from 'react';
import { BUILD_ID, CLIENT_CONTRACT, DEPLOYMENT_RELOAD_IN_DEV } from '@/buildConstants';
import {
  createDeploymentReloadMonitor,
  type DeploymentReloadOutcomeType,
  type DeploymentReloadReasonType,
} from '@/utils/deploymentReload';
import { hasDraftBlockers, subscribeToDraftBlockers } from '@/local-sync/draftRegistry';

type UseDeploymentReloadOptions = {
  enabled?: boolean;
  intervalMs?: number;
  /**
   * Calls its listener each time the reader moves to another screen, and returns the unsubscribe.
   * A new deployment is taken there, never under the screen being read (I-0781).
   */
  subscribeToNavigation?: (onNavigate: () => void) => () => void;
};

/** A newer build is waiting: which one, whether the server has outgrown this page, and how to take it. */
export interface IPendingDeployment {
  buildId: string;
  /** T-0329: the deployment no longer serves this bundle; it reloads once no draft holds it. */
  incompatible: boolean;
  reloadNow: () => DeploymentReloadOutcomeType;
}

/** Where the reason for an outgrown client's reload waits for the page that replaces it. */
const RELOAD_REASON_KEY = 'featit:deploymentReloadReason';

function reloadFor(reason: DeploymentReloadReasonType): void {
  if (reason === 'incompatible') {
    try {
      sessionStorage.setItem(RELOAD_REASON_KEY, reason);
    } catch {
      // No storage: the page still reloads, it just cannot say why afterwards.
    }
  }
  window.location.reload();
}

/** T-0329: whether this page replaced one the server had outgrown. Pure: `clearIncompatibleReload` forgets it. */
export function reloadedBecauseIncompatible(): boolean {
  try {
    return sessionStorage.getItem(RELOAD_REASON_KEY) === 'incompatible';
  } catch {
    return false;
  }
}

/** Said once: a later reload of the same tab is not explained by this one. */
export function clearIncompatibleReload(): void {
  try {
    sessionStorage.removeItem(RELOAD_REASON_KEY);
  } catch {
    // Nothing stored, nothing to clear.
  }
}

/**
 * I-0593 RS-2: a run of failed update checks is a fault a person reading the console can see, never a
 * reload that silently stopped. Said once when the run starts, and once when it ends.
 */
function reportCheckFailing(reason: string | null): void {
  if (reason === null) console.info('[featit] deployment update check recovered');
  else console.warn(`[featit] deployment update check failing: ${reason}`);
}

export function useDeploymentReload({
  enabled = import.meta.env.PROD || DEPLOYMENT_RELOAD_IN_DEV,
  intervalMs,
  subscribeToNavigation,
}: UseDeploymentReloadOptions = {}): IPendingDeployment | null {
  const [pending, setPending] = useState<{ buildId: string; incompatible: boolean } | null>(null);
  const reloadNowRef = useRef<() => DeploymentReloadOutcomeType>(() => 'nothing-pending');

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const monitor = createDeploymentReloadMonitor({
      initialBuildId: BUILD_ID,
      clientContract: CLIENT_CONTRACT,
      intervalMs,
      reload: reloadFor,
      shouldDeferReload: hasDraftBlockers,
      onReloadDeferred: (buildId, incompatible) => setPending({ buildId, incompatible }),
      onCheckFailing: reportCheckFailing,
    });
    reloadNowRef.current = monitor.reloadIfPending;
    const stop = monitor.start();
    const unsubscribe = subscribeToNavigation?.(monitor.reloadIfPending);
    // T-0329: an outgrown client waits for a draft only as long as the draft is unsaved.
    const unsubscribeDrafts = subscribeToDraftBlockers(() => void monitor.reloadIfIncompatible());
    return () => {
      unsubscribeDrafts();
      unsubscribe?.();
      stop();
      reloadNowRef.current = () => 'nothing-pending';
    };
  }, [enabled, intervalMs, subscribeToNavigation]);

  if (!pending) return null;
  return { ...pending, reloadNow: () => reloadNowRef.current() };
}
